import _ from "lodash";
import {useMemo} from "react";
import {FarmInsightItem, FieldInsightItem} from "../types/fieldInsightItem";
import {Farm} from "../types/domain/farm";
import {Field} from "../types/domain/field";
import {Crop} from "../types/domain/crop";
import {useActiveFarmFields} from "../store/activeFarmFieldsStore";
import {FieldStatus, useFieldStatusById} from "./fieldsHooks";
import {getLatestCropId} from "../utils/fieldUtils";
import {useCrops} from "./cropsHooks";
import {useActiveCompanyId} from "./companiesHooks";
import {useCompanyFarms} from "./farmsHooks";
import {useActiveCompanyFields} from "./activeCompanyFieldsHooks";

export const useFarmsInsightsItems = () => {
    const {cropsById} = useCrops();
    const companyId = useActiveCompanyId();
    const fields = useActiveCompanyFields();
    const farmsState = useCompanyFarms(companyId);
    const statusByFieldId = useFieldStatusById();

    const farmInsightItems = useMemo(
        () =>
            farmsState?.data?.farms?.map((farm) =>
                mapFarmToFarmInsightItem(farm, fields?.data?.fields ?? [], cropsById, statusByFieldId),
            ),
        [farmsState?.data?.farms, fields, cropsById, statusByFieldId],
    );

    return farmInsightItems;
};

export const useFieldsInsightsItems = () => {
    const {cropsById} = useCrops();
    const fields = useActiveFarmFields();
    const statusByFieldId = useFieldStatusById();
    const items = useMemo(
        () => fields.map((field) => mapFieldToFieldInsightItem(field, cropsById, statusByFieldId)),
        [fields, cropsById, statusByFieldId],
    );

    return {items};
};

export const useMergedFieldsInsightsItems = (fields: Field[]): FieldInsightItem[] => {
    const {cropsById} = useCrops();
    const statusByFieldId = useFieldStatusById();

    return fields.map((field) => mapFieldToFieldInsightItem(field, cropsById, statusByFieldId));
};

const mapFarmToFarmInsightItem = (
    farm: Farm,
    fields: Field[],
    cropsMap: Record<string, Crop>,
    statusByFieldId: Record<string, FieldStatus>,
): FarmInsightItem => {
    const fieldItems = fields
        .filter((field) => field.farmId === farm.id)
        .map((field) => mapFieldToFieldInsightItem(field, cropsMap, statusByFieldId));

    return {
        type: "FARM",
        name: farm.name,
        moistureAlerts: fieldItems.filter((item) => item.isMoistureAlert).length,
        growthAlerts: fieldItems.filter((item) => item.isGrowthAlert).length,
        crops: _.uniqBy(fieldItems, "crop")
            .map((item) => item.crop)
            .filter((item) => item) as string[],
        areaInHa: _.sumBy(fieldItems, "areaInHa"),
        fields: fieldItems,
        farm,
    };
};

const mapFieldToFieldInsightItem = (
    field: Field,
    cropsById: Record<string, Crop>,
    statusByFieldId: Record<string, FieldStatus>,
): FieldInsightItem => {
    const cropId = getLatestCropId(field);
    return {
        type: "FIELD",
        name: field.name,
        moisture: statusByFieldId[field.id]?.soilMoisture ?? null,
        isMoistureAlert: statusByFieldId[field.id]?.isSoilMoistureAlert ?? false,
        growth: statusByFieldId[field.id]?.cropGrowth ?? null,
        isGrowthAlert: statusByFieldId[field.id]?.isCropGrowthAlert ?? false,
        crop: cropId ? cropsById[cropId]?.name : undefined,
        areaInHa: field.areaInHa,
        field,
        timeUntilNextIrrigation: statusByFieldId[field.id]?.timeUntilNextIrrigation ?? null,
    };
};
